import type { RouteComponentProps } from '@reach/router';
import { navigate } from 'gatsby';
import React, { useMemo } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import { CurationStatusBadge } from '@components/CurationStatusBadge';
import { TableStatus, TableNote } from '@components/DataTable';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import NullStateDiv from '@components/NullStateDiv';
import { RecordSourcePill } from '@components/RecordSourcePill';
import SelfReportedPill from '@components/SelfReportedPill';

import * as ROUTES from '@constants/routes';

import { useGetHospitalizations } from '@hooks/api/hospitalization';
import { useSession } from '@hooks/useSession';

import { TableContainer } from '@layouts/Layouts/TableLayout';

import type { CurationStatus, ResourceSourceType } from 'generated/types';

const messages = defineMessages({
  noPast: {
    id: 'hospitalizationsMain.noPast',
    defaultMessage: 'No Known Hospitalization Records',
  },
  title: {
    id: 'hospitalizationsMain.title',
    defaultMessage: 'Hospitalizations',
  },
});

const Hospitalizations: React.FC<RouteComponentProps> = () => {
  const { formatMessage } = useIntl();
  const {
    state: { isClient, isPractitioner, isVitall },
  } = useSession();
  const { data, loading } = useGetHospitalizations();

  const tableData = useMemo(
    () =>
      data.map((a) => ({
        id: a.id,
        viewedStatus: a.viewedStatus,
        visitReason: a.visitReason,
        hasNotes: Boolean(a.recordNotes.length),
        facility: a.facility,
        location: a.address,
        hcpName: a.practitioner,
        visitDate: a.getVisitDateStr(),
        selfReported: a.selfReported,
        curationStatus: a.curationStatus,
        recordSource: a.recordSource,
      })),
    [data]
  );

  return (
    <ContentLoader loading={loading}>
      <TableContainer>
        {data.length === 0 ? (
          <NullStateDiv>{formatMessage(messages.noPast)}</NullStateDiv>
        ) : (
          <MuiDataTable
            columns={[
              {
                name: 'viewedStatus',
                options: {
                  customHeadLabelRender: () => null,
                  customBodyRender: (value: boolean) => (
                    <TableStatus value={value} />
                  ),
                },
              },
              {
                name: 'curationStatus',
                label: 'Curation Status',
                className: 'small',
                options: {
                  customBodyRender: (status: CurationStatus) => (
                    <CurationStatusBadge status={status} />
                  ),
                  filter: isVitall,
                  filterType: 'multiselect',
                  display: isVitall,
                },
              },
              {
                name: 'recordSource',
                label: 'Record Source',
                className: 'small',
                options: {
                  customBodyRender: (source: ResourceSourceType) => (
                    <RecordSourcePill type={source} />
                  ),
                  filter: isVitall || isPractitioner,
                  filterType: 'multiselect',
                  display: isVitall || isPractitioner,
                },
              },
              {
                name: 'visitReason',
                label: 'Visit Reason',
                className: 'blue-hover',
                options: {
                  searchable: true,
                  sort: true,
                },
              },
              {
                name: 'hasNotes',
                desktopOnly: true,
                options: {
                  customHeadLabelRender: () => null,
                  customBodyRender: (value: boolean) => (
                    <TableNote value={value} />
                  ),
                },
              },
              {
                name: 'location',
                className: 'small-light',
                label: 'Location',
                options: {
                  filter: true,
                  searchable: true,
                  sort: true,
                },
              },
              {
                name: 'facility',
                className: 'small-light',
                label: 'Facility',
                options: {
                  filter: true,
                  searchable: true,
                  sort: true,
                },
              },
              {
                name: 'hcpName',
                label: 'Provider Name',
                className: 'small',
                desktopOnly: true,
                options: {
                  filter: true,
                  searchable: true,
                  sort: true,
                },
              },
              {
                name: 'visitDate',
                className: 'date',
                label: 'Visit Date',
                options: {
                  filter: true,
                  sort: true,
                },
              },
              {
                name: 'selfReported',
                options: {
                  customHeadLabelRender: () => null,
                  customBodyRender: (value: boolean) => (
                    <SelfReportedPill value={value} />
                  ),
                  display: isClient,
                  filter: isClient,
                },
              },
            ]}
            data={tableData}
            options={{
              onRowClick: (_rowData, { dataIndex }) => {
                if (dataIndex <= tableData.length - 1) {
                  const row = tableData[dataIndex];
                  navigate(`${ROUTES.HOSPITALIZATIONS}${row.id}`);
                }
              },
              sortOrder: {
                name: 'visitDate',
                direction: 'desc',
              },
              filter: true,
              search: true,
              sort: true,
            }}
            pageParam="hosp"
          />
        )}
      </TableContainer>
    </ContentLoader>
  );
};

export default withErrorBoundary(Hospitalizations, {
  FallbackComponent: ErrorBoundaryFallback,
});
